/**
 * Onto the ledger — after checking they are not already on it.
 *
 * Moving somebody who already pays creates a second account for one customer:
 * two owners, two renewal dates, two people ringing them, and a month later
 * an argument about which figure is right. The check is the whole reason this
 * window exists; the move is the easy part.
 *
 * Three answers, three different screens, and none of them guesses:
 *
 *   already a customer   the account is shown, and the move is off. There is
 *                        nothing useful to create.
 *   not a customer       what is about to be created is shown first, then the
 *                        button.
 *   could not tell       the accounts API did not answer. This says so and
 *                        refuses, because "we could not check" waved through
 *                        as "not a client" is exactly how the duplicate gets
 *                        made.
 *
 * Matched on email and nothing else — see clientMatch.js on the server for
 * why names and phone numbers are not used.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import { companyName, contactOf } from '../../domain/crm.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';

/** One fact about what is being created. */
const Line = ({ label, children }) => (
  <div className="mtc-line">
    <dt>{label}</dt>
    <dd>{children || <span className="dim">—</span>}</dd>
  </div>
);

export default function MoveToClientSheet({ id, from = 'deal', onClose }) {
  const { crm, S, checkClient, convertDeal, setSheet } = useApp();
  const navigate = useNavigate();

  const deal = crm.deals.find(d => d.id === id);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let gone = false;
    setBusy(true);
    checkClient(id)
      .then(r => { if (!gone) setCheck(r); })
      .finally(() => { if (!gone) setBusy(false); });
    return () => { gone = true; };
  }, [id, checkClient]);

  if (!deal) return null;

  const ct = deal.contact ? contactOf(crm, deal.contact) : null;
  const email = (check && check.email) || deal.leadEmail || (ct && ct.email) || '';
  const existing = check && check.client;
  /* Not the same as "no client". See the docblock. */
  const unsure = check && !check.known;
  const lead = from === 'pipeline';

  const move = async () => {
    setMoving(true);
    await convertDeal(id, from);
    setMoving(false);
    onClose();
  };

  const openClient = () => {
    onClose();
    navigate('/clients');
    /* The account itself, not just the page it is on — the point of showing
       them the match is that they can look at it. */
    setSheet({ kind: 'clientView', id: existing.id });
  };

  return (
    <Sheet
      icon="users"
      tone={existing ? 'g-amber' : 'g-green'}
      title={lead ? 'Move this lead to Clients' : 'Move this deal to Clients'}
      onClose={onClose}
      closeLabel={existing ? 'Leave it' : 'Cancel'}
      onSave={existing || unsure || busy ? undefined : move}
      saveLabel={moving ? 'Moving…' : 'Move to Clients'}
      bodyClass="mtc-form"
      hero={(
        <div className="mtc-hero">
          <b>{deal.title}</b>
          <span>{email || 'No email address on this row'}</span>
        </div>
      )}
    >
      {busy ? (
        <div className="mtc-wait">
          <span className="spin" aria-hidden="true" />
          <p>Checking the client book…</p>
        </div>
      ) : existing ? (
        <>
          {/* The answer, before anything else on the screen. */}
          <section className="mtc-hit">
            <div className="mtc-hitic"><Icon name="alert" /></div>
            <div>
              <b>They are already a client</b>
              <p>
                {`${existing.name} is on the ledger under ${email}. `}
                {existing.where === 'app'
                  ? 'The account is in the clients app.'
                  : 'The account was created here.'}
              </p>
            </div>
          </section>

          <p className="mtc-why">
            Moving this across would make a second account for one customer —
            two owners, two renewal dates, and two people calling them. Nothing
            has been changed.
          </p>

          <div className="mtc-acts">
            <button type="button" className="btn primary" onClick={openClient}>
              <Icon name="eye" />Open {existing.name}
            </button>
            {/* The row is still sitting on a board. This is the other thing
                somebody wants at this moment, and without it they close the
                window and go looking for it. */}
            <button
              type="button"
              className="btn"
              onClick={() => { onClose(); setSheet({ kind: 'notInterested', id }); }}
            >
              <Icon name="x" />Close the {lead ? 'lead' : 'deal'} off
            </button>
          </div>
        </>
      ) : unsure ? (
        <section className="mtc-hit unsure">
          <div className="mtc-hitic"><Icon name="alert" /></div>
          <div>
            <b>The client book could not be read</b>
            <p>
              Nothing has been moved. Checking is the point of this window, and
              a move made without it is how one customer ends up with two
              accounts. Try again in a minute.
            </p>
          </div>
        </section>
      ) : (
        <>
          <section className="mtc-ok">
            <div className="mtc-okic"><Icon name="check" /></div>
            <div>
              <b>Not on the ledger</b>
              <p>{email ? `No account under ${email}.` : 'No email to check against — nothing matched.'}</p>
            </div>
          </section>

          <dl className="mtc-what">
            <Line label="Account name">{companyName(crm, deal.cid) || deal.title}</Line>
            <Line label="Contact">{(ct && ct.name) || null}</Line>
            <Line label="Email">{email || null}</Line>
            <Line label="Plan">{deal.plan || null}</Line>
            <Line label="Billing">{deal.cycle || 'Monthly'}</Line>
            <Line label="Value">{deal.value ? `${S.currency} ${Number(deal.value).toLocaleString()}` : null}</Line>
            {/* The one that matters to a manager reading this later: the
                account keeps whoever was carrying the lead, not whoever
                happened to press the button. */}
            <Line label="Owner">{deal.owner || <span className="dim">nobody — set one first</span>}</Line>
            <Line label="Marked as">
              <span className="mtc-tag">{lead ? 'From pipeline' : 'From deals'}</span>
            </Line>
          </dl>

          <p className="mtc-why">
            The {lead ? 'lead' : 'deal'} stays where it is and is marked as
            moved. The new account is tagged so the Clients page can show and
            filter what came across from here.
          </p>
        </>
      )}
    </Sheet>
  );
}
